import { memo, useState } from 'react'
import type { User } from '../../api/types'
import { Avatar } from '../../components/Avatar'
import { BotTag } from '../../components/BotTag'
import { Icon } from '../../components/Icon'
import { useIsOnline } from '../../hooks/useOnline'
import { isBotUser } from '../../lib/bots'
import { userName, type UserMap } from '../../lib/channels'
import { formatClock, formatDateTime, toneFor } from '../../lib/format'
import { useUi } from '../../state/ui'
import { BotProfileButton } from '../bots/BotName'
import { deleteOutboxRow, retryOutboxRow } from '../../sync/outbox'
import type { TimelineMessage } from '../../sync/outboxMerge'
import { AttachmentList } from './AttachmentList'
import { ContextQuote } from './ContextQuote'
import { useConversation } from './ConversationContext'
import { EditForm } from './EditForm'
import { ForwardedLabel } from './ForwardedLabel'
import { answerQuestion } from './messageActions'
import { messageRules } from './messageRules'
import type { Receipt } from './receipts'
import { MessageChecklist } from './MessageChecklist'
import { MessageOptions } from './MessageOptions'
import { MessageSheet } from './MessageSheet'
import { MessageText } from './MessageText'
import { MessageTools } from './MessageTools'
import { isChecklist, isQuestion } from './questions'
import { Reactions } from './Reactions'
import { ReplyQuote } from './ReplyQuote'
import { SeenBy } from './SeenBy'
import { useMessageSheetTriggers } from './useMessageSheetTriggers'
import { attachmentKind } from '../../lib/files'

interface Props {
  item: TimelineMessage
  continued: boolean
  highlighted: boolean
  seenBy: string[] | null
  /** What your own message says about itself: sent, read, read by everyone (see receipts). None on its own. */
  receipt?: Receipt | null
  users: UserMap
  /** This device's answer to the message, when it is a bot's question: sent or still sending. */
  answeredWith?: string | null
  /** This device has answered the message, when it is a bot's question or checklist (sent or still sending). */
  doneHere?: boolean
  /** An answer directly under the question it answers (timeline): no quote of it, the question is right above. */
  answersAbove?: boolean
}

function MessageItemInner({ item, continued, highlighted, seenBy, receipt = null, users, answeredWith = null, doneHere = false, answersAbove = false }: Props) {
  const { me, channel, canModerate, editing: edit, startEdit, endEdit } = useConversation()
  const { message, pending } = item
  const editing = edit?.messageId === message.id
  const [pickerOpen, setPickerOpen] = useState(false)
  const mine = message.authorId === me.id
  const author: User | undefined = users.get(message.authorId)
  const authorName = mine ? 'You' : userName(users, message.authorId)
  const bot = !mine && isBotUser(author)
  const openBotProfile = useUi((s) => s.openBotProfile)
  const online = useIsOnline(message.authorId)
  const deleted = Boolean(message.deletedAt)
  const question = isQuestion(message)
  const questionLabel = `question-${message.id}`
  const checklist = question && isChecklist(message)
  const rules = messageRules(message, channel, me.id, canModerate)
  // Messages still sending, deleted or being edited have no actions.
  const actionable = !pending && !deleted && !editing
  const sheet = useMessageSheetTriggers({ message, rules, actionable, onOpen: () => setPickerOpen(false) })
  // In a chat between two people every message is from one of them: no avatars, no names above the bubbles.
  const oneToOne = channel.type === 'dm' || channel.type === 'bot_dm'
  // A run of messages is named once, except a bot's: nobody should have to scroll up to see a message is not a person's.
  const showAuthor = !mine && !oneToOne && (!continued || bot) && !deleted

  const ticks = receipt === 'sent' || receipt === 'read' || receipt === 'all'
  const tickIcon = <Icon name={receipt === 'sent' ? 'check' : 'checkDouble'} />
  const clock = formatClock(message.createdAt)
  const edited = Boolean(message.editedAt) && !deleted
  // The time sits at the end of the message: beside the words when they leave room, on its own line when they do not.
  const footer = (
    <span className="bubble-footer">
      {edited ? (
        <span className="edited" title={`Edited ${formatDateTime(message.editedAt!)}`}>
          (edited)
        </span>
      ) : null}
      <time dateTime={message.createdAt} title={formatDateTime(message.createdAt)}>
        {clock}
      </time>
      {ticks ? (
        <span className={`receipt ${receipt}`} role="img" aria-label={receipt === 'sent' ? 'Sent' : receipt === 'read' ? 'Read' : 'Read by everyone'}>
          {tickIcon}
        </span>
      ) : null}
    </span>
  )
  const authorLine = showAuthor ? (
    <span className={`bubble-author ${toneFor(message.authorId)}`}>
      {bot ? (
        <BotProfileButton userId={message.authorId} label={`About ${authorName}`} className="message-author-button">
          <strong>{authorName}</strong>
        </BotProfileButton>
      ) : (
        <strong>{authorName}</strong>
      )}
      {bot ? <BotTag /> : null}
    </span>
  ) : null

  const classes = [
    'message',
    mine ? 'mine' : '',
    continued ? 'continued' : '',
    pending ? 'pending' : '',
    pending?.state === 'failed' ? 'failed' : '',
    highlighted ? 'is-target' : '',
    sheet.request ? 'is-held' : '',
    // A photo or video sent on its own: the picture is the message, its time and ticks on it.
    !message.body && message.attachments.length > 0 && message.attachments.every((a) => ['image', 'video'].includes(attachmentKind(a.contentType))) ? 'is-visual' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <article
      ref={sheet.rowRef}
      className={classes}
      data-message-id={message.id}
      tabIndex={-1}
      aria-label={`${authorName}${bot ? ' (bot)' : ''}, ${formatDateTime(message.createdAt)}`}
      {...sheet.rowProps}
    >
      {sheet.swipeable ? (
        <span className="swipe-reply" aria-hidden="true">
          <Icon name="reply" />
        </span>
      ) : null}
      {mine || oneToOne ? null : (
      <Avatar
        user={author}
        id={message.authorId}
        name={authorName}
        online={!mine && online}
        // The bot's name below is the keyboard-accessible way to its profile; the icon is a larger target.
        onClick={bot ? () => openBotProfile(message.authorId) : undefined}
      />
      )}
      <div className="message-content">
        {actionable ? (
          <MessageTools
            message={message}
            rules={rules}
            pickerOpen={pickerOpen}
            onTogglePicker={() => setPickerOpen((o) => !o)}
            sheetOpen={Boolean(sheet.request)}
            onOpenSheet={sheet.openFrom}
          />
        ) : null}

        {message.context && !deleted ? <ContextQuote context={message.context} /> : null}
        {message.replyToId && !deleted && !answersAbove ? <ReplyQuote replyToId={message.replyToId} /> : null}

        {deleted ? (
          <div className="bubble deleted">
            <span className="bubble-text">This message was deleted</span>
            {footer}
          </div>
        ) : editing ? (
          <EditForm
            ref={edit?.form}
            message={message}
            focusRequests={edit?.focusRequests}
            onDone={() => {
              // Back to where the edit started (the message box, for ↑), unless focus moved on while it saved.
              const row = sheet.rowRef.current
              const active = document.activeElement
              if (!active || active === document.body || row?.contains(active)) {
                ;(edit?.returnFocus?.isConnected ? edit.returnFocus : row)?.focus({ preventScroll: true })
              }
              endEdit(message.id)
            }}
          />
        ) : (
          <>
            {message.forwarded ? <ForwardedLabel forwarded={message.forwarded} /> : null}
            {authorLine && !message.body ? <div className="media-author">{authorLine}</div> : null}
            {message.attachments.length ? <AttachmentList message={message} pending={Boolean(pending)} /> : null}
            {message.body ? (
              <div className={checklist ? 'bubble has-checklist' : question ? 'bubble has-options' : 'bubble'}>
                {authorLine}
                <span className="bubble-text" id={question ? questionLabel : undefined}>
                  {/* A forward's @mentions were written for another conversation: they notify nobody, so none is yours here. */}
                  <MessageText body={message.body} meUsername={message.forwarded ? null : me.username} />
                </span>
                {checklist ? (
                  <MessageChecklist
                    checklist={message}
                    labelId={questionLabel}
                    doneHere={doneHere}
                    botAccepts={channel.bot?.acceptsMessages ?? false}
                    botName={authorName}
                  />
                ) : question ? (
                  <MessageOptions
                    question={message}
                    labelId={questionLabel}
                    localAnswer={answeredWith}
                    botAccepts={channel.bot?.acceptsMessages ?? false}
                    botName={authorName}
                    onAnswer={(option) => void answerQuestion(message, option)}
                  />
                ) : null}
                {footer}
              </div>
            ) : (
              <div className="media-footer">{footer}</div>
            )}
          </>
        )}

        {!pending && !deleted ? (
          <Reactions
            message={message}
            canReact={rules.react}
            pickerOpen={pickerOpen}
            onClosePicker={() => setPickerOpen(false)}
            onMoreReactions={sheet.openEmojiFrom}
          />
        ) : null}

        {pending ? (
          pending.state === 'failed' ? (
            <div className="send-status failed" role="status">
              <Icon name="alert" />
              <span>Not sent{pending.error ? `: ${pending.error}` : ''}</span>
              <div className="send-status-actions">
                <button type="button" className="button small" onClick={() => void retryOutboxRow(pending.id)}>
                  <Icon name="refresh" />
                  Try again
                </button>
                <button type="button" className="button small danger-button" onClick={() => void deleteOutboxRow(pending.id)}>
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="send-status" role="status">
              <span className="spinner" style={{ width: 12, height: 12 }} aria-hidden="true" />
              {pending.attempts > 0 ? 'Waiting to send…' : 'Sending…'}
            </div>
          )
        ) : null}

        {seenBy && seenBy.length ? <SeenBy userIds={seenBy} users={users} /> : null}
      </div>

      {sheet.request ? (
        <MessageSheet message={message} rules={rules} request={sheet.request} onClose={sheet.close} onEdit={() => startEdit(message)} />
      ) : null}
    </article>
  )
}

export const MessageItem = memo(MessageItemInner)
